import { Center, Loader, Stack, Text } from "@mantine/core";
import { doc } from "firebase/firestore";
import { FunctionComponent, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useFirestore, useFirestoreDocData, useUser } from "reactfire";

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Protected route that checks if user has admin privileges.
 * Checks Firestore for is_admin: true field.
 */
export const AdminRoute: FunctionComponent<AdminRouteProps> = ({
  children,
}) => {
  const { data: user, status: userStatus } = useUser();
  const firestore = useFirestore();

  // Always call hooks at the top level - use a stable dummy reference
  const userDoc = user
    ? doc(firestore, "users", user.uid)
    : doc(firestore, "users", "anonymous");
  const { data: userData, status: docStatus } = useFirestoreDocData(userDoc, {
    // Don't subscribe if no user
    suspense: false,
    idField: "id",
  });

  // If user not authenticated, redirect to home
  if (userStatus === "loading") {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check if Firestore doc is still loading
  if (docStatus === "loading") {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Verifying admin access...
          </Text>
        </Stack>
      </Center>
    );
  }

  // Check if user has admin privileges
  const isAdmin = userData?.is_admin === true;

  if (!isAdmin) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Text size="xl" fw={700} c="red">
            Access Denied
          </Text>
          <Text size="sm" c="dimmed">
            You do not have admin privileges.
          </Text>
          <Text
            size="sm"
            c="blue"
            style={{ cursor: "pointer" }}
            onClick={() => (window.location.href = "/")}
          >
            Return to home
          </Text>
        </Stack>
      </Center>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;
